import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box, Button, Link, TextField, Typography,
  Alert, CircularProgress,
} from '@mui/material';
import { MarkEmailRead } from '@mui/icons-material';
import { useForgotPasswordMutation } from '../../store/api/authApi';

const schema = yup.object({
  email: yup.string().email('Enter a valid email address').required('Email address is required'),
});

type FormData = yup.InferType<typeof schema>;

export const ForgotPasswordPage: React.FC = () => {
  const [sent, setSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [serverError, setServerError] = useState('');

  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    setServerError('');
    try {
      await forgotPassword({ email: data.email }).unwrap();
      setSubmittedEmail(data.email);
      setSent(true);
    } catch (err: any) {
      setServerError(err?.data?.detail ?? 'Something went wrong. Please try again.');
    }
  };

  if (sent) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <MarkEmailRead sx={{ fontSize: 56, color: '#39D6B0', mb: 2 }} />
        <Typography variant="h4" fontWeight={800} sx={{ color: '#F8FAFC', letterSpacing: '-0.02em', mb: 1 }}>
          Check your email
        </Typography>
        <Typography variant="body2" sx={{ color: '#94A3B8', mb: 3 }}>
          If <strong style={{ color: '#F8FAFC' }}>{submittedEmail}</strong> is registered, we've sent a password reset link.
          It expires in 1 hour.
        </Typography>
        <Typography variant="body2" sx={{ color: '#94A3B8' }}>
          Back to{' '}
          <Link component={RouterLink} to="/auth/login" sx={{ color: '#39D6B0', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            Sign in
          </Link>
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={800} textAlign="center" sx={{ color: '#F8FAFC', letterSpacing: '-0.02em', mb: 0.75 }}>
        Reset your password
      </Typography>
      <Typography variant="body2" sx={{ color: '#94A3B8', textAlign: 'center', mb: 3.5 }}>
        Enter your work email and we'll send you a reset link
      </Typography>

      {serverError && (
        <Alert severity="error" sx={{ mb: 2.5, bgcolor: 'rgba(239, 68, 68, 0.12)', color: '#EF4444', border: '1px solid rgba(239, 68, 68, 0.28)', borderRadius: '12px' }}>
          {serverError}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, display: 'block', mb: 0.75 }}>
            Work email address
          </Typography>
          <TextField
            {...register('email')}
            placeholder="you@company.com"
            type="email"
            fullWidth
            autoComplete="email"
            autoFocus
            error={!!errors.email}
            helperText={errors.email?.message}
          />
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading}
          sx={{
            mt: 1,
            mb: 2.5,
            py: 1.4,
            fontWeight: 700,
            fontSize: '0.95rem',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #39D6B0 0%, #20C878 100%)',
            color: '#071321',
            boxShadow: '0 2px 10px rgba(57, 214, 176, 0.25)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4ce0bd 0%, #2bd987 100%)',
              boxShadow: '0 4px 16px rgba(57, 214, 176, 0.4)',
            },
          }}
        >
          {isLoading ? <CircularProgress size={22} sx={{ color: '#071321' }} /> : 'Send reset link'}
        </Button>
      </Box>

      <Typography variant="body2" textAlign="center" sx={{ color: '#94A3B8' }}>
        Remember your password?{' '}
        <Link component={RouterLink} to="/auth/login" sx={{ color: '#39D6B0', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
          Sign in
        </Link>
      </Typography>
    </Box>
  );
};
